import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Per-material routing — keep all material-specific knobs in one place so each
// caller only needs to pass `material="diamond" | "color_stone" | "pearl"`.
// (Wave 3.18 bug-fix: previously the dialog was hard-coded to /diamonds and
// silently created classifications in the wrong collection when invoked from
// Color Stones / Pearls pages.)
const MATERIAL_CONFIG = {
  diamond: {
    endpoint: '/diamonds/classifications',
    title: 'New Diamond Classification',
    schemaPlaceholder: 'e.g. D-RB',
  },
  color_stone: {
    endpoint: '/color-stones/classifications',
    title: 'New Colour Stone Classification',
    schemaPlaceholder: 'e.g. TNZ-CUSH',
  },
  pearl: {
    endpoint: '/pearls/classifications',
    title: 'New Pearl Classification',
    schemaPlaceholder: 'e.g. PRL-RND',
  },
};

export function ClassificationFormDialog({ open, onOpenChange, companyId, onCreated, material = 'diamond' }) {
  const cfg = MATERIAL_CONFIG[material] || MATERIAL_CONFIG.diamond;
  const empty = { schema_code: '', shape_name: '', description: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!form.schema_code.trim() || !form.shape_name.trim()) {
      toast.error('Schema code and shape name are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(cfg.endpoint, {
        ...form,
        schema_code: form.schema_code.toUpperCase(),
        company_id: companyId,
      });
      toast.success(`Classification "${data.schema_code}" created`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Classification create failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inline-classification-dialog">
        <DialogHeader>
          <DialogTitle>{cfg.title}</DialogTitle>
          <DialogDescription>Create a shape category used as a prefix for packet numbers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Schema code (2–15 chars, A–Z 0–9 ‐)</Label>
            <Input value={form.schema_code}
              onChange={(e) => setForm({ ...form, schema_code: e.target.value.toUpperCase() })}
              placeholder={cfg.schemaPlaceholder} maxLength={15} data-testid="inline-classification-schema-input" />
          </div>
          <div>
            <Label>Shape name</Label>
            <Input value={form.shape_name} onChange={(e) => setForm({ ...form, shape_name: e.target.value })}
              placeholder="e.g. Round Brilliant" data-testid="inline-classification-shape-input" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={saving} data-testid="inline-classification-submit-button">{saving ? 'Saving…' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClassificationFormDialog;
